// Supplier Performance Settings API
import api, { type AxiosResponse, type PaginatedResponse } from './client';
import type {
  SettingsScope,
  SupplierPerformanceSettings,
  SupplierPerformanceSettingsUpdate,
  SiteKPISettings,
  SupplierRatingThreshold,
  CreateRatingThresholdData,
  OTIFGradeThreshold,
  CreateOTIFGradeThresholdData,
  PPMGradeThreshold,
  GradeColour,
  CreatePPMGradeThresholdData,
  OTIFScoreMapping,
  PPMScoreMapping,
  QueryParams,
} from './types';

// CR-5883 Push C: resolver-driven payload returned by
// /core/site-kpi-settings/effective/. month_keys is rotated to start on the
// configured fiscal month; year_label is ready to drop into chart headers.
export interface EffectiveKPISettings {
  year_type: 'fiscal' | 'calendar';
  fiscal_year_end_month: number;
  fiscal_year_start_month: number;
  month_keys: string[];
  year_label: string;
  source: string | null;
  scope_id: number | null;
  scope_name: string | null;
}

/** CR-6266: which KPI a time-based target applies to. */
export type KPITargetKind = 'otif' | 'otd' | 'ppm';

/**
 * CR-6266: a KPI target over a date range. Where no range covers a date, the
 * default target on the performance settings applies.
 */
export interface KPITargetRule {
  id: number;
  kpi: KPITargetKind;
  kpi_display: string;
  /** A percentage for OTIF / OTD; a whole number for PPM (decimal string). */
  target_value: string;
  /** First day the target applies (YYYY-MM-DD). */
  effective_from: string;
  /** Last day it applies, inclusive; null = no end. */
  effective_to: string | null;
}

/**
 * CR-6266: the OTIF / OTD targets the Scheduled and PO Lines dashboards stamp
 * on each week and month row — the targets in force at the row's end.
 */
export interface PeriodKPITargets {
  otif_target?: number;
  otd_target?: number;
}

/** CR-6266: the editable fields of a target range. */
export interface KPITargetRuleInput {
  kpi: KPITargetKind;
  target_value: string;
  effective_from: string;
  effective_to: string | null;
}

/**
 * CR-7274: which settings row to read or write.
 *
 * Omit it entirely for the caller's own configuration context — that is the
 * default path every pre-existing caller uses, and it is unchanged.
 *
 * `{ scope: 'group', id }` is the one that matters: a save there reaches every
 * site under the group that has no override of its own, which is the whole
 * point of the ticket.
 */
export interface ScopeSelection {
  scope: SettingsScope;
  /** Required for `site` and `group`; ignored for `platform`. */
  id?: number;
}

/** Axios params for a scope, or `undefined` for the default context. */
export const scopeParams = (
  target?: ScopeSelection,
): Record<string, string | number> | undefined => {
  if (!target) return undefined;
  // The micro-cache in client.ts keys on url + params, so each scope is
  // cached separately — switching scope cannot serve another scope's values.
  return target.id === undefined
    ? { scope: target.scope }
    : { scope: target.scope, id: target.id };
};

export const performanceSettingsAPI = {
  // Get supplier performance settings. CR-7274: optionally for an explicit
  // scope; without one this is the caller's own context, exactly as before.
  getSettings: (
    target?: ScopeSelection,
  ): Promise<AxiosResponse<SupplierPerformanceSettings>> =>
    api.get('/core/settings/performance/', { params: scopeParams(target) }),

  // Customer review 11 Sep (item 2): the viewer's rating grade bands —
  // the ones Admin Setup → Performance → Grade Thresholds edits, with label and
  // palette colour. Read-only and open to every customer-side role (the
  // settings endpoint above is admin-only, which is how a plain customer user
  // ended up on a hard-coded scale). Empty for suppliers: each customer's
  // bands ride on the supplier-side rows instead. CR-8215: `group` asks for a
  // group view's bands (the supplier cards grade a group view on the group).
  getRatingBands: (params?: { group?: number }): Promise<AxiosResponse<SupplierRatingThreshold[]>> =>
    api.get('/core/settings/performance/rating-bands/', { params }),

  // CR-6269: the viewer's OTIF grade bands — the ones OTIF Settings edits —
  // for the supplier list's OTIF quick-filter chips. Same resolution and the
  // same empty-for-suppliers rule as getRatingBands.
  getOTIFBands: (): Promise<AxiosResponse<OTIFGradeThreshold[]>> =>
    api.get('/core/settings/performance/otif-bands/'),

  // Update supplier performance settings. The view returns the FULL settings
  // object directly (SupplierPerformanceSettingsSerializer.data) — the old
  // `{ message, data }` type here was wrong and made callers read
  // `response.data.data`, which is undefined at runtime.
  updateSettings: (
    data: SupplierPerformanceSettingsUpdate,
    target?: ScopeSelection,
  ): Promise<AxiosResponse<SupplierPerformanceSettings>> =>
    api.patch('/core/settings/performance/', data, { params: scopeParams(target) }),

  /**
   * CR-7274: stop overriding at this scope, so it inherits again.
   *
   * Deletes the row rather than blanking it — a blanked row still wins the
   * resolution walk while reading as inherited, which is exactly the ambiguity
   * this ticket removes. The bands go with it; the inherited set takes over on
   * the next read.
   */
  clearOverride: (
    target: ScopeSelection,
  ): Promise<AxiosResponse<{ detail: string; cleared: boolean }>> =>
    api.delete('/core/settings/performance/', { params: scopeParams(target) }),

  // Site KPI Settings
  getSiteKPISettings: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: SiteKPISettings[] }>> =>
    api.get('/core/site-kpi-settings/', { params }),

  getSiteKPISettingsForSite: (siteId: number): Promise<AxiosResponse<SiteKPISettings>> =>
    api.get(`/core/site-kpi-settings/for-site/${siteId}/`),

  getSiteKPISettingsForAccessibleSites: (): Promise<AxiosResponse<SiteKPISettings[]>> =>
    api.get('/core/site-kpi-settings/for-accessible-sites/'),

  // CR-5883 Push C: resolver-driven effective KPI for the current scope.
  // Returns the ordered month_keys + a ready-to-render year_label so chart
  // axes and KPI tiles can honour the configured fiscal calendar without
  // re-implementing the resolver walk on the FE.
  getEffectiveKPISettings: (params?: { site?: number }): Promise<AxiosResponse<EffectiveKPISettings>> =>
    api.get('/core/site-kpi-settings/effective/', { params }),

  updateSiteKPISettings: (id: number, data: Partial<SiteKPISettings>): Promise<AxiosResponse<SiteKPISettings>> =>
    api.patch(`/core/site-kpi-settings/${id}/`, data),

  // CR-7274 rework: every editor below takes the configured scope. The band,
  // palette and target-range ViewSets resolve `?scope=&id=` exactly as the
  // settings endpoint does; with no scope they use the caller's own context.
  // Before this, each of them wrote to the viewer's HOME site whatever the
  // "Configure settings for" picker said.

  // Rating Thresholds
  getRatingThresholds: (params?: QueryParams, target?: ScopeSelection): Promise<AxiosResponse<{ count: number; results: SupplierRatingThreshold[] }>> =>
    api.get('/core/rating-thresholds/', { params: { ...params, ...scopeParams(target) } }),

  createRatingThreshold: (data: CreateRatingThresholdData, target?: ScopeSelection): Promise<AxiosResponse<SupplierRatingThreshold>> =>
    api.post('/core/rating-thresholds/', data, { params: scopeParams(target) }),

  updateRatingThreshold: (id: number, data: Partial<SupplierRatingThreshold>, target?: ScopeSelection): Promise<AxiosResponse<SupplierRatingThreshold>> =>
    api.patch(`/core/rating-thresholds/${id}/`, data, { params: scopeParams(target) }),

  deleteRatingThreshold: (id: number, target?: ScopeSelection): Promise<AxiosResponse<void>> =>
    api.delete(`/core/rating-thresholds/${id}/`, { params: scopeParams(target) }),

  resetRatingThresholds: (performanceSettingsId: number, target?: ScopeSelection): Promise<AxiosResponse<{ message: string; thresholds: SupplierRatingThreshold[] }>> =>
    api.post('/core/rating-thresholds/reset_defaults/', { performance_settings_id: performanceSettingsId }, { params: scopeParams(target) }),

  // CR-7079: the shared grade colour palette. One row per grade; every
  // scorecard resolves its displayed colour from these, so a change here
  // cascades rather than needing repeating in each settings tab.
  getGradeColours: (params?: QueryParams, target?: ScopeSelection): Promise<AxiosResponse<{ count: number; results: GradeColour[] }>> =>
    api.get('/core/grade-colours/', { params: { ...params, ...scopeParams(target) } }),

  updateGradeColour: (id: number, data: Partial<GradeColour>, target?: ScopeSelection): Promise<AxiosResponse<GradeColour>> =>
    api.patch(`/core/grade-colours/${id}/`, data, { params: scopeParams(target) }),

  resetGradeColours: (target?: ScopeSelection): Promise<AxiosResponse<GradeColour[]>> =>
    api.post('/core/grade-colours/reset-defaults/', undefined, { params: scopeParams(target) }),

  // CR-6267: OTIF Colour Grade Bands. Separate table and endpoint from the
  // rating thresholds above — the two share a shape, never a row.
  getOTIFGradeThresholds: (params?: QueryParams, target?: ScopeSelection): Promise<AxiosResponse<{ count: number; results: OTIFGradeThreshold[] }>> =>
    api.get('/core/otif-grade-thresholds/', { params: { ...params, ...scopeParams(target) } }),

  createOTIFGradeThreshold: (data: CreateOTIFGradeThresholdData, target?: ScopeSelection): Promise<AxiosResponse<OTIFGradeThreshold>> =>
    api.post('/core/otif-grade-thresholds/', data, { params: scopeParams(target) }),

  updateOTIFGradeThreshold: (id: number, data: Partial<OTIFGradeThreshold>, target?: ScopeSelection): Promise<AxiosResponse<OTIFGradeThreshold>> =>
    api.patch(`/core/otif-grade-thresholds/${id}/`, data, { params: scopeParams(target) }),

  deleteOTIFGradeThreshold: (id: number, target?: ScopeSelection): Promise<AxiosResponse<void>> =>
    api.delete(`/core/otif-grade-thresholds/${id}/`, { params: scopeParams(target) }),

  resetOTIFGradeThresholds: (target?: ScopeSelection): Promise<AxiosResponse<OTIFGradeThreshold[]>> =>
    api.post('/core/otif-grade-thresholds/reset-defaults/', undefined, { params: scopeParams(target) }),

  // CR-6652: PPM Colour Grade Bands. Separate table and endpoint from the
  // OTIF grade thresholds above — the two share a shape, never a row.
  getPPMGradeThresholds: (params?: QueryParams, target?: ScopeSelection): Promise<AxiosResponse<{ count: number; results: PPMGradeThreshold[] }>> =>
    api.get('/core/ppm-grade-thresholds/', { params: { ...params, ...scopeParams(target) } }),

  createPPMGradeThreshold: (data: CreatePPMGradeThresholdData, target?: ScopeSelection): Promise<AxiosResponse<PPMGradeThreshold>> =>
    api.post('/core/ppm-grade-thresholds/', data, { params: scopeParams(target) }),

  updatePPMGradeThreshold: (id: number, data: Partial<PPMGradeThreshold>, target?: ScopeSelection): Promise<AxiosResponse<PPMGradeThreshold>> =>
    api.patch(`/core/ppm-grade-thresholds/${id}/`, data, { params: scopeParams(target) }),

  deletePPMGradeThreshold: (id: number, target?: ScopeSelection): Promise<AxiosResponse<void>> =>
    api.delete(`/core/ppm-grade-thresholds/${id}/`, { params: scopeParams(target) }),

  resetPPMGradeThresholds: (target?: ScopeSelection): Promise<AxiosResponse<PPMGradeThreshold[]>> =>
    api.post('/core/ppm-grade-thresholds/reset-defaults/', undefined, { params: scopeParams(target) }),

  // CR-6266: time-based OTIF / OTD / PPM targets (ranges over the default).
  getKPITargetRules: (kpi: KPITargetKind, target?: ScopeSelection): Promise<AxiosResponse<PaginatedResponse<KPITargetRule>>> =>
    api.get('/core/kpi-target-rules/', { params: { kpi, page_size: 100, ...scopeParams(target) } }),

  createKPITargetRule: (data: KPITargetRuleInput, target?: ScopeSelection): Promise<AxiosResponse<KPITargetRule>> =>
    api.post('/core/kpi-target-rules/', data, { params: scopeParams(target) }),

  updateKPITargetRule: (id: number, data: Partial<KPITargetRuleInput>, target?: ScopeSelection): Promise<AxiosResponse<KPITargetRule>> =>
    api.patch(`/core/kpi-target-rules/${id}/`, data, { params: scopeParams(target) }),

  deleteKPITargetRule: (id: number, target?: ScopeSelection): Promise<AxiosResponse<void>> =>
    api.delete(`/core/kpi-target-rules/${id}/`, { params: scopeParams(target) }),

  // OTIF Score Mappings
  getOTIFScoreMappings: (params?: QueryParams, target?: ScopeSelection): Promise<AxiosResponse<{ count: number; results: OTIFScoreMapping[] }>> =>
    api.get('/core/otif-score-mappings/', { params: { ...params, ...scopeParams(target) } }),

  updateOTIFScoreMapping: (id: number, data: Partial<OTIFScoreMapping>, target?: ScopeSelection): Promise<AxiosResponse<OTIFScoreMapping>> =>
    api.patch(`/core/otif-score-mappings/${id}/`, data, { params: scopeParams(target) }),

  resetOTIFScoreMappings: (performanceSettingsId: number, target?: ScopeSelection): Promise<AxiosResponse<{ message: string; mappings: OTIFScoreMapping[] }>> =>
    api.post('/core/otif-score-mappings/reset_defaults/', { performance_settings_id: performanceSettingsId }, { params: scopeParams(target) }),

  // PPM Score Mappings
  getPPMScoreMappings: (params?: QueryParams, target?: ScopeSelection): Promise<AxiosResponse<{ count: number; results: PPMScoreMapping[] }>> =>
    api.get('/core/ppm-score-mappings/', { params: { ...params, ...scopeParams(target) } }),

  updatePPMScoreMapping: (id: number, data: Partial<PPMScoreMapping>, target?: ScopeSelection): Promise<AxiosResponse<PPMScoreMapping>> =>
    api.patch(`/core/ppm-score-mappings/${id}/`, data, { params: scopeParams(target) }),

  resetPPMScoreMappings: (performanceSettingsId: number, target?: ScopeSelection): Promise<AxiosResponse<{ message: string; mappings: PPMScoreMapping[] }>> =>
    api.post('/core/ppm-score-mappings/reset_defaults/', { performance_settings_id: performanceSettingsId }, { params: scopeParams(target) }),

  // Supplier OTIF Override (CR-3375). DORMANT — not wired; see PERF-SETTINGS-HIGHS (server answers 410).
  updateSupplierOTIFOverride: (
    supplierProfileId: number,
    data: {
      use_custom_otif_window?: boolean;
      otif_days_early_allowed?: number;
      otif_days_late_allowed?: number;
    }
  ): Promise<AxiosResponse<{
    message: string;
    data: {
      id: number;
      company_id: number;
      company_name: string;
      use_custom_otif_window: boolean;
      otif_days_early_allowed: number;
      otif_days_late_allowed: number;
    };
  }>> => api.patch(`/core/supplier-otif-override/${supplierProfileId}/`, data),
};
